import { sessionManager } from "@/broadcastChannel/sessionManager";
import { toast } from "@/components/ui/use-toast";
import {
  clearVolatileBrowserCaches,
  isQuotaExceededError,
} from "@/libs/browserStorage";
import type { QueryKey } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

type CacheId = string | QueryKey;

export type UseFetchDataV2Options = {
  browserCache?: boolean;
  browserCacheKey?: string;
  browserCacheNamespace?: string;
  browserCacheTtlMs?: number;
  cacheId?: CacheId;
  gcTime?: number;
  noCache?: boolean;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
};

type BrowserCacheRecord<T> = {
  savedAt: number;
  data: T;
};

type FetchDataOptions = {
  force?: boolean;
  gcTime?: number;
  queryKey?: QueryKey;
  staleTime?: number;
};

const isCancelledRequestError = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { name?: string; message?: string; code?: string };
  const message = String(candidate.message ?? "").toLowerCase();

  return (
    candidate.name === "AbortError" ||
    candidate.name === "CancelledError" ||
    candidate.code === "ERR_CANCELED" ||
    message === "cancellederror" ||
    message === "cancelled error" ||
    message === "cancelederror" ||
    message === "canceled error" ||
    message.includes("cancelled") ||
    message.includes("canceled")
  );
};

// 🔒 verrou pour éviter plusieurs refresh simultanés
let refreshPromise: Promise<string> | null = null;
const waitForToken = async (maxWaitMs = 500, intervalMs = 50) => {
  const endTime = Date.now() + maxWaitMs;
  let token = sessionManager.getToken();
  while (!token && Date.now() < endTime) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    token = sessionManager.getToken();
  }
  return token;
};

function safeBrowserStorage() {
  if (typeof window === "undefined") return null;

  try {
    const key = "__verdeo_fetch_cache_test__";
    window.sessionStorage.setItem(key, "1");
    window.sessionStorage.removeItem(key);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function hashText(value: string) {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

function decodeJwtPayload(token: string | null) {
  if (!token || typeof window === "undefined") return null;

  const [, payload] = token.split(".");
  if (!payload) return null;

  try {
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload =
      normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
    const decoded = JSON.parse(window.atob(paddedPayload));
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
}

function getBrowserCacheUserScope() {
  const payload = decodeJwtPayload(sessionManager.getToken());
  const userIdentifier =
    payload?.id ??
    payload?.userId ??
    payload?.sub ??
    payload?.email ??
    payload?.username ??
    "anonymous";

  return String(userIdentifier).replace(/[^a-zA-Z0-9_.:-]/g, "_");
}

function buildBrowserCacheKey(
  uri: string,
  {
    browserCacheKey,
    browserCacheNamespace,
  }: Pick<UseFetchDataV2Options, "browserCacheKey" | "browserCacheNamespace">,
) {
  const namespace = String(browserCacheNamespace || "fetchDataV2").replace(
    /[^a-zA-Z0-9_.:-]/g,
    "_",
  );
  const rawKey = browserCacheKey ? `${browserCacheKey}:${uri}` : uri;

  return `verdeo:${namespace}:${getBrowserCacheUserScope()}:${hashText(rawKey)}`;
}

function readBrowserCache<T>(
  uri: string,
  options: UseFetchDataV2Options,
): T | undefined {
  if (!options.browserCache || !uri) return undefined;

  const storage = safeBrowserStorage();
  if (!storage) return undefined;

  try {
    const raw = storage.getItem(buildBrowserCacheKey(uri, options));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as BrowserCacheRecord<T>;
    const savedAt = Number(parsed?.savedAt || 0);
    if (
      options.browserCacheTtlMs &&
      savedAt > 0 &&
      Date.now() - savedAt > options.browserCacheTtlMs
    ) {
      return undefined;
    }
    return parsed?.data;
  } catch {
    return undefined;
  }
}

function writeBrowserCache<T>(
  uri: string,
  data: T,
  options: UseFetchDataV2Options,
) {
  if (!options.browserCache || !uri || typeof data === "undefined") return;

  const storage = safeBrowserStorage();
  if (!storage) return;

  try {
    storage.setItem(
      buildBrowserCacheKey(uri, options),
      JSON.stringify({
        savedAt: Date.now(),
        data,
      } satisfies BrowserCacheRecord<T>),
    );
  } catch (error) {
    if (isQuotaExceededError(error)) {
      clearVolatileBrowserCaches(storage);
      try {
        storage.setItem(
          buildBrowserCacheKey(uri, options),
          JSON.stringify({
            savedAt: Date.now(),
            data,
          } satisfies BrowserCacheRecord<T>),
        );
        return;
      } catch {
        // Le cache navigateur reste optionnel.
      }
    }
    // Le cache navigateur est une optimisation. React Query reste la source en mémoire.
  }
}

export function readFetchDataV2BrowserCache<T>(
  uri: string,
  options: UseFetchDataV2Options,
): T | undefined {
  return readBrowserCache<T>(uri, options);
}

export function writeFetchDataV2BrowserCache<T>(
  uri: string,
  data: T,
  options: UseFetchDataV2Options,
) {
  writeBrowserCache(uri, data, options);
}

// Fonction pour rafraîchir le token
const refreshToken = async (): Promise<string> => {
  if (!refreshPromise) {
    const refresh = sessionManager.getRefreshToken();
    if (!refresh) throw new Error("No refresh token available");

    refreshPromise = fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/token/refresh`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
        credentials: "include",
      }
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to refresh token");
        const data = await res.json();
        sessionManager.login({
          jwtToken: data.token,
          refreshToken: data.refresh_token,
        });
        return data.token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Wrapper fetch avec gestion du token
const fetchWithToken = async (
  endpoint: string,
  token: string,
  noCache = false,
) => {
  const response = await fetch(endpoint, {
    method: "GET",
    cache: noCache ? "no-store" : "default",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (response.status === 401) throw new Error("TOKEN_EXPIRED");
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

  return response.json();
};

const buildPrimaryQueryKey = (endpoint: string, cacheId?: CacheId) =>
  Array.isArray(cacheId)
    ? cacheId
    : cacheId
      ? [cacheId, endpoint]
      : ["fetchDataV2", endpoint];

const buildFetchQueryKey = (uri: string, queryKey?: QueryKey) =>
  Array.isArray(queryKey) ? queryKey : ["fetchDataV2", uri];

// Hook principal
export function useFetchDataV2<T>(
  endpoint: string,
  cacheIdOrOptions?: CacheId | UseFetchDataV2Options,
  staleTime?: number,
  gcTime?: number,
) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [apiReadyEndpoint, setApiReadyEndpoint] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isOptionsObject =
    cacheIdOrOptions &&
    typeof cacheIdOrOptions === "object" &&
    !Array.isArray(cacheIdOrOptions);
  const options: UseFetchDataV2Options = isOptionsObject
    ? (cacheIdOrOptions as UseFetchDataV2Options)
    : {};
  const cacheId = isOptionsObject
    ? options.cacheId
    : (cacheIdOrOptions as CacheId | undefined);
  const effectiveStaleTime = options.staleTime ?? staleTime;
  const effectiveGcTime = options.gcTime ?? gcTime;
  const primaryQueryKey = buildPrimaryQueryKey(
    endpoint,
    cacheId,
  );
  const queryKey = options.noCache
    ? [...primaryQueryKey, "no-cache"]
    : primaryQueryKey;
  const browserCacheOptions = useMemo<UseFetchDataV2Options>(
    () => ({
      browserCache: options.noCache ? false : !!options.browserCache,
      browserCacheKey: options.browserCacheKey,
      browserCacheNamespace: options.browserCacheNamespace,
      browserCacheTtlMs: options.browserCacheTtlMs,
    }),
    [
      options.browserCache,
      options.browserCacheKey,
      options.browserCacheNamespace,
      options.browserCacheTtlMs,
      options.noCache,
    ],
  );
  const endpointBrowserCache = useMemo(
    () => readBrowserCache<T>(endpoint, browserCacheOptions),
    [browserCacheOptions, endpoint],
  );

  useEffect(() => {
    setApiReadyEndpoint(null);
  }, [endpoint]);

  const executeAuthenticatedFetch = useCallback(async (uri: string) => {
    let token = sessionManager.getToken();
    if (!token) {
      token = await waitForToken();
    }
    if (!token) {
      sessionManager.logout();
      throw new Error("No access token found");
    }

    try {
      return await fetchWithToken(uri, token, options.noCache);
    } catch (err: any) {
      if (err.message === "TOKEN_EXPIRED") {
        setIsRefreshing(true);
        try {
          token = await refreshToken();
          return await fetchWithToken(uri, token, options.noCache);
        } catch (_err) {
          toast({
            title: "Votre session a expiré, veuillez vous reconnecter.",
          });
          sessionManager.logout();
          throw new Error("Session expired");
        } finally {
          setIsRefreshing(false);
        }
      }

      throw err;
    }
  }, [options.noCache]);

  // --- React Query pour endpoint principal ---
  const query = useQuery<T, Error>({
    queryKey,
    queryFn: async () => {
      const freshData = await executeAuthenticatedFetch(endpoint);
      setApiReadyEndpoint(endpoint);
      return freshData;
    },
    retry: 0,
    enabled: !!endpoint,
    refetchInterval: options.refetchInterval,
    refetchOnMount: options.browserCache ? "always" : true,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    initialData: endpointBrowserCache,
    initialDataUpdatedAt:
      typeof endpointBrowserCache !== "undefined" ? 0 : undefined,
    staleTime: options.noCache
      ? 0
      : typeof effectiveStaleTime === "number" && Number.isFinite(effectiveStaleTime)
        ? Math.max(effectiveStaleTime, 0)
        : 0,
    gcTime: options.noCache
      ? 0
      : typeof effectiveGcTime === "number" && Number.isFinite(effectiveGcTime)
        ? Math.max(effectiveGcTime, 0)
        : undefined,
  });

  useEffect(() => {
    if (
      typeof query.data === "undefined" ||
      apiReadyEndpoint !== endpoint
    ) return;
    writeBrowserCache(endpoint, query.data, browserCacheOptions);
  }, [apiReadyEndpoint, browserCacheOptions, endpoint, query.data]);

  // --- fetchData(uri) pour fetch arbitraire ---
  const fetchData = useCallback(async (
    uri: string,
    fetchOptions?: FetchDataOptions,
  ) => {
    if (!uri) return null;

    if (options.noCache) {
      try {
        return await executeAuthenticatedFetch(uri);
      } catch (err: any) {
        if (isCancelledRequestError(err)) {
          return null;
        }
        toast({
          title: err.message || "Erreur lors de la récupération des données.",
        });
        return null;
      }
    }

    const queryKey = buildFetchQueryKey(uri, fetchOptions?.queryKey);

    try {
      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => executeAuthenticatedFetch(uri),
        // fetchData alimente souvent des formulaires modifiables : l'API doit
        // toujours être consultée avant qu'une valeur puisse être réutilisée.
        staleTime: 0,
        gcTime: fetchOptions?.gcTime,
      });
      writeBrowserCache(uri, data as T, browserCacheOptions);
      return data;
    } catch (err: any) {
      if (isCancelledRequestError(err)) {
        return null;
      }
      toast({
        title: err.message || "Erreur lors de la récupération des données.",
      });
      return null;
    }
  }, [browserCacheOptions, executeAuthenticatedFetch, options.noCache, queryClient]);

  const prefetchData = useCallback(async (
    uri: string,
    fetchOptions?: FetchDataOptions,
  ) => {
    if (!uri) return null;

    if (options.noCache) {
      try {
        return await executeAuthenticatedFetch(uri);
      } catch {
        return null;
      }
    }

    const queryKey = buildFetchQueryKey(uri, fetchOptions?.queryKey);

    try {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () => executeAuthenticatedFetch(uri),
        staleTime: fetchOptions?.staleTime,
        gcTime: fetchOptions?.gcTime,
      });
      const data = (queryClient.getQueryData(queryKey) as T | undefined) ?? null;
      if (data !== null) {
        writeBrowserCache(uri, data, browserCacheOptions);
      }
      return data;
    } catch {
      return readBrowserCache<T>(uri, browserCacheOptions) ?? null;
    }
  }, [browserCacheOptions, executeAuthenticatedFetch, options.noCache, queryClient]);

  return {
    data: query.data, // les données de React Query
    loading: query.isLoading, // indicateur de chargement
    error: query.error?.message || null, // message d’erreur
    isFetching: query.isFetching, // si une requête est en cours
    isApiReady: !endpoint || apiReadyEndpoint === endpoint,
    refetch: query.refetch,
    fetchData, // la fonction fetchData(uri) réintégrée
    prefetchData,
    isRefreshing, // si le token est en train d’être refreshé
  };
}
